import { useActionSheet } from "@expo/react-native-action-sheet";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { KeyboardAvoidingView, ScrollView, StyleSheet } from "react-native";
import { block, house } from "../../../src/assets/images";
import {
  AddressForm,
  LocationDisclaimerSheet,
  PresenceInfoQuestion,
  loaderRef,
} from "../../../src/components";
import { useAppDispatch, useAppSelector, useCreateOrUpdateAddress } from "../../../src/hooks";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";
import { setUserData } from "../../../src/store/user";

import {
  extrudeAdditionalInfo,
  isWeb,
  useDeletePropertyMutation,
  useGetUserProfileLazyQuery,
} from "@stepapp/core";
import { ApartmentType } from "@stepapp/core/apiTypes";
import {
  Block,
  BottomSheet,
  Button,
  Divider,
  Image,
  Input,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
  useHeaderHeight,
} from "@stepapp/ui";

import { useTranslation } from "react-i18next";
import HomeInfoItem from "./HomeInfoItem";

const imageMapping = {
  [ApartmentType.Apartment]: block,
  [ApartmentType.House]: house,
} as const;

const EditHomePage = () => {
  const appDispatch = useAppDispatch();
  const { back } = useRouter();
  const { t } = useTranslation();
  const { propertyId = "" } = useLocalSearchParams<{ propertyId: string }>();

  const { id: userId, properties } = useAppSelector(({ user }) => user);
  const homeProfile = useAppSelector(({ homeProfile }) => homeProfile);
  const { city, localizationId } = useAppSelector(({ appConfig }) => appConfig);
  const property = properties?.find((property) => property.propertyId === propertyId);

  const { showActionSheetWithOptions } = useActionSheet();
  const [sheetVisible, setSheetVisible] = useState(false);
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    trigger,
  } = useFormContext<ReservationFormParams>();

  const [meta, address] = watch(["meta", "address"]);

  const [deleteProperty] = useDeletePropertyMutation();
  const [getUserProfile, { loading: getUserProfileLoading }] = useGetUserProfileLazyQuery({
    variables: {
      isCustomerProfile: true,
    },
    fetchPolicy: "network-only",
  });
  const [createOrUpdateAddress, { loading }] = useCreateOrUpdateAddress({
    isUserLogged: !!userId,
    property,
    localizationId,
  });

  const disabled = useMemo(
    () => !isValid || loading || getUserProfileLoading,
    [isValid, getUserProfileLoading, loading],
  );

  const toggleSheetVisibility = useCallback(() => setSheetVisible((v) => !v), []);

  const handleChangeHomeType = useCallback(
    (type: ApartmentType) => {
      setValue("address.type", type);
      toggleSheetVisibility();
    },
    [setValue, toggleSheetVisibility],
  );

  const handleDeleteProperty = useCallback(() => {
    showActionSheetWithOptions(
      {
        title: t("screens.homeEdit.confirmDeleteProperty"),
        options: [t("screens.homeEdit.deleteProperty"), t("common.goBack")],
        destructiveButtonIndex: 0,
        cancelButtonIndex: 1,
        useModal: true,
      },
      async (index) => {
        const shouldDelete = index === 0;

        if (shouldDelete) {
          loaderRef.current?.showLoader();
          const isDeleted = await deleteProperty({
            variables: { data: { apartmentId: propertyId } },
          });

          if (isDeleted) {
            const { data: userData } = await getUserProfile();

            userData && appDispatch(setUserData(userData?.getMyProfile));
            loaderRef.current?.hideLoader();
            back();
          } else {
            loaderRef.current?.hideLoader();
            alert(t("common.error"));
          }
        }
      },
    );
  }, [
    back,
    appDispatch,
    showActionSheetWithOptions,
    getUserProfile,
    deleteProperty,
    propertyId,
    t,
  ]);

  const onSubmit = useCallback(
    async ({ meta, address }: ReservationFormParams) => {
      const additionalInfo = extrudeAdditionalInfo(address?.notes) || "";
      const descriptionAnnotation = meta?.willCustomerBePresent
        ? `${t("pages.serviceDateAndAddress.iWillBeThere")}`
        : meta?.keyInfo || "";
      const newValues = {
        ...address,
        floorArea: address?.floorArea || 0,
        notes: `${additionalInfo}\n\n${descriptionAnnotation}`,
      };

      const isSuccess = await createOrUpdateAddress(newValues);

      if (isSuccess) {
        back();
      } else {
        setNotAvailableLocation(true);
      }
    },
    [createOrUpdateAddress, back, t],
  );

  return (
    <>
      <KeyboardAvoidingView behavior="padding" keyboardVerticalOffset={useHeaderHeight() + 30}>
        <ScrollView>
          <Block marginBottom={1} paddingTop={isWeb ? 4 : 2}>
            <Typography variant="newH2">
              {propertyId ? t("screens.homeEdit.editAddress") : t("screens.homeEdit.addAddress")}
            </Typography>
          </Block>

          <Typography type="secondary" variant="lightLabel">
            {t("screens.homeEdit.provideInformation")}
          </Typography>
          <Block marginTop={3}>
            <Input
              error={errors?.address?.name?.message}
              onBlur={() => trigger("address.name")}
              onChangeText={(value) =>
                setValue("address.name", value, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              placeholder={t("components.input.placeholder.propertyName")}
              value={`${address?.name}`}
            />
            <Typography type="secondary" variant="lightLabel">
              {t("screens.homeEdit.chooseFriendlyName")}
            </Typography>
          </Block>

          <Block borderRadius={2} marginBottom={2.5} marginTop={3} style={styles.homeInfoContainer}>
            <HomeInfoItem
              editable
              handleEditPress={() => toggleSheetVisibility()}
              label={t("screens.homeEdit.homeType")}
              testID="homeTypeButton"
              value={address?.type ? t("screens.homeEdit.type", { context: address.type }) : ""}
            />
          </Block>

          <AddressForm
            city={address?.city || city}
            doorCode={address?.doorCode || ""}
            errors={errors}
            floorArea={address?.floorArea || homeProfile?.floorArea || 0}
            postalOrZipCode={address?.postalOrZipCode || ""}
            setValue={setValue}
            trigger={trigger}
            streetAddress1={address?.streetAddress1 || ""}
            streetAddress2={address?.streetAddress2 || ""}
            notes={address?.notes || ""}
          />

          <Divider />

          <PresenceInfoQuestion
            isHomeEditScreen
            errors={errors}
            keyInfo={meta?.keyInfo}
            setValue={setValue}
            willCustomerBePresent={meta?.willCustomerBePresent}
          />

          <Divider />

          <Block marginBottom={8} marginTop={4}>
            <Button
              disabled={disabled}
              label={t("screens.editProfile.updateInfo")}
              loading={loading || getUserProfileLoading}
              onPress={handleSubmit(onSubmit)}
              type="primary"
            />
            {!!propertyId && (
              <Block marginTop={2}>
                <Button
                  disabled={disabled}
                  label={t("screens.homeEdit.deleteProperty")}
                  onPress={() => handleDeleteProperty()}
                  type="secondary"
                />
              </Block>
            )}
          </Block>
        </ScrollView>
      </KeyboardAvoidingView>

      <BottomSheet
        contentStyle={styles.bottomSheetHomeType}
        toggle={toggleSheetVisibility}
        visible={sheetVisible}
      >
        <Block marginVertical={2}>
          <Block justifySpace row>
            <Typography variant="newH2">{t("screens.accommodation.title")}</Typography>
            <Touchable onPress={() => setSheetVisible(false)}>
              <Block style={styles.closeIcon}>
                <SvgIcon color={palette.secondary.grey} name="cross" size={38} />
              </Block>
            </Touchable>
          </Block>
          <Typography type="secondary">{t("screens.accommodation.description")}</Typography>
        </Block>

        {Object.values(ApartmentType).map((type) => (
          <Touchable
            key={type}
            onPress={() => handleChangeHomeType(type)}
            style={styles.homeTypeButton}
          >
            <Image
              contentFit="contain"
              height={spacing.unit * 6.5}
              source={imageMapping[type]}
              style={styles.image}
              width={spacing.unit * 6.5}
            />
            <Typography variant="button">
              {t("screens.accommodation.type", { context: type })}
            </Typography>
          </Touchable>
        ))}
      </BottomSheet>

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />
    </>
  );
};

export default EditHomePage;

const styles = StyleSheet.create({
  bottomSheetHomeType: {
    bottom: 0,
    padding: spacing.unit * 2.5,
  },
  closeIcon: {
    backgroundColor: palette.common.black,
  },
  homeInfoContainer: {
    backgroundColor: palette.secondary.main,
  },
  homeTypeButton: {
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
    marginVertical: spacing.unit,
    paddingHorizontal: spacing.unit * 3,
    paddingVertical: spacing.unit * 1.75,
    flexDirection: "row",
    alignItems: "center",
  },
  image: {
    height: spacing.unit * 6.5,
    marginRight: spacing.unit * 3.75,
  },
});
